import { createLogger, CustomLogger } from '@/lib/logger';
import { Swiper } from 'swiper/types';

export class ProductsCarousel extends HTMLElement {
  private swiperRootElem: HTMLElement | null;
  private slides: HTMLElement[] | null;
  private tabName: string;
  private groupName: string;
  private log: CustomLogger;
  private swiper: Swiper | null;

  // Whether the carousel is a shell (i.e. no products, but ready for mounting via JS)
  private shell: boolean;

  constructor() {
    super();

    this.swiperRootElem = null;
    this.slides = null;
    this.tabName = '';
    this.groupName = '';
    this.log = createLogger('products-carousel');
    this.swiper = null;

    this.shell = this.hasAttribute('data-shell');

    if (!this.shell) {
      this.hydrate();
    }
  }

  hydrate() {
    const swiperRootElem = this.querySelector<HTMLElement>('.swiper');

    if (!swiperRootElem) {
      this.log.error('Could not find swiper root element');
      return;
    }

    this.swiperRootElem = swiperRootElem;

    const tabName = this.getAttribute('data-tab');

    if (!tabName) {
      this.log.error('Could not find tab name');
      return;
    }

    this.tabName = tabName;

    const groupName = this.getAttribute('data-group');

    if (!groupName) {
      this.log.error('Could not find group name');
      return;
    }

    this.groupName = groupName;

    const slides = this.querySelectorAll<HTMLElement>('.swiper-slide');

    if (!slides.length) {
      this.log.error('Could not find slides');
      return;
    }

    this.slides = Array.from(slides);

    this.initSwiper();

    // @ts-ignore
    if (window?.StampedFn?.loadBadges) {
      // @ts-ignore
      window.StampedFn.loadBadges();
    }
  }

  static switchVisibleNavigation(groupName: string, tabName: string) {
    // Hide other navigations
    const otherNavigations = document.querySelectorAll(
      `[data-carousel-navigation][data-group="${groupName}"]`,
    );
    otherNavigations.forEach((navigation) => {
      navigation.classList.remove('active');
    });

    // Activate navigation
    const navigation = document.querySelector(
      `[data-carousel-navigation][data-group="${groupName}"][data-tab="${tabName}"]`,
    );
    if (!navigation) {
      console.warn(`No navigation found for tab ${tabName} in group ${groupName}`);
      return;
    }

    navigation.classList.add('active');
  }

  updateNavigation(swiper: Swiper, tabName: string) {
    const currentIndex = (swiper.activeIndex || 0) + 1;
    const totalGroups = swiper.slides.length;

    const currentCountElem = document.querySelector(
      `[data-carousel-navigation][data-tab="${tabName}"] .carousel-current-index`,
    );
    const totalCountElem = document.querySelector(
      `[data-carousel-navigation][data-tab="${tabName}"] .carousel-total-index`,
    );

    if (!currentCountElem || !totalCountElem) {
      this.log.error('Could not find current count or total count element');
      return;
    }

    currentCountElem.textContent = currentIndex.toString();
    totalCountElem.textContent = totalGroups.toString();

    // swiper.update();
  }

  initSwiper() {
    const instance = this;
    this.swiper = new window.Swiper(this.swiperRootElem!, {
      slidesPerView: 'auto',
      slidesPerGroup: 1,
      spaceBetween: 8,
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      navigation: {
        nextEl: `[data-carousel-navigation][data-tab="${this.tabName}"] .carousel-navigation-button.next`,
        prevEl: `[data-carousel-navigation][data-tab="${this.tabName}"] .carousel-navigation-button.prev`,
      },
      breakpoints: {
        768: {
          spaceBetween: 16,
        },
      },
      on: {
        init(swiper) {
          instance.updateNavigation(swiper, instance.tabName);
        },
        update(swiper) {
          instance.updateNavigation(swiper, instance.tabName);
        },
        slideChange(swiper) {
          instance.updateNavigation(swiper, instance.tabName);
        },
        resize(swiper) {
          swiper.slideToClosest();
        },
      },
    });
  }

  getSlides() {
    return this.slides;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  if (customElements.get('products-carousel')) return;

  customElements.define('products-carousel', ProductsCarousel);
});
